/**
 * Markdown → HTML pipeline for Tiptap input.
 *
 * - Preserves frontmatter (stripped from body, returned separately).
 * - Walks MDAST to compute stable block IDs (sha256(type:normalize(text))).
 * - Emits HTML with `data-block-id` attributes on every block node.
 *
 * Round-trip with htmlToMd is tested in src/features/viewer/markdown/roundtrip.test.ts.
 */

import { computeBlockId } from "./blockId";

interface MdResult {
  html: string;
  frontmatter: string | null;
  blockIds: string[];
}

const FM_RE = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/;

/**
 * Minimal MD → HTML converter sufficient for read-only display in Tiptap.
 * Supports: headings, paragraphs, lists (ul/ol), blockquotes, code blocks,
 * inline code, bold, italic, links, images, hr, tables (GFM), task lists.
 *
 * Tiptap will then take this HTML and produce its ProseMirror state.
 */
export async function mdToHtml(source: string): Promise<MdResult> {
  let body = source;
  let frontmatter: string | null = null;
  const m = FM_RE.exec(source);
  if (m) {
    frontmatter = m[1] ?? null;
    body = source.slice(m[0].length);
  }

  const lines = body.split(/\r?\n/);
  const blockIds: string[] = [];
  const out: string[] = [];

  let i = 0;
  while (i < lines.length) {
    const line = lines[i] ?? "";

    // Heading
    const hMatch = /^(#{1,6})\s+(.+?)\s*$/.exec(line);
    if (hMatch) {
      const level = hMatch[1]?.length ?? 1;
      const text = hMatch[2] ?? "";
      const id = await computeBlockId(`h${level}`, text);
      blockIds.push(id);
      out.push(`<h${level} data-block-id="${id}">${inline(text)}</h${level}>`);
      i += 1;
      continue;
    }

    // Fenced code
    const fence = /^```([\w-]*)\s*$/.exec(line);
    if (fence) {
      const lang = fence[1] ?? "";
      const buf: string[] = [];
      i += 1;
      while (i < lines.length && !/^```\s*$/.test(lines[i] ?? "")) {
        buf.push(lines[i] ?? "");
        i += 1;
      }
      i += 1; // skip closing ```
      const text = buf.join("\n");
      const id = await computeBlockId("code", text);
      blockIds.push(id);
      out.push(
        `<pre data-block-id="${id}"><code class="language-${escapeAttr(lang)}">${escapeHtml(text)}</code></pre>`,
      );
      continue;
    }

    // Blockquote
    if (/^>\s?/.test(line)) {
      const buf: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i] ?? "")) {
        buf.push((lines[i] ?? "").replace(/^>\s?/, ""));
        i += 1;
      }
      const text = buf.join("\n");
      const id = await computeBlockId("blockquote", text);
      blockIds.push(id);
      out.push(`<blockquote data-block-id="${id}"><p>${inline(text)}</p></blockquote>`);
      continue;
    }

    // Task list / unordered list
    if (/^[-*+]\s+/.test(line)) {
      const items: string[] = [];
      const legacyItems: string[] = [];
      const itemIds: string[] = [];
      while (i < lines.length && /^[-*+]\s+/.test(lines[i] ?? "")) {
        const ln = (lines[i] ?? "").replace(/^[-*+]\s+/, "");
        const itemId = await computeBlockId("li", ln);
        itemIds.push(itemId);
        const tm = /^\[([ xX])\]\s+(.*)$/.exec(ln);
        if (tm) {
          const checked = tm[1]?.toLowerCase() === "x";
          const itemHtml = `<input type="checkbox" disabled ${checked ? "checked" : ""}/> ${inline(tm[2] ?? "")}`;
          legacyItems.push(`<li data-task="${checked ? "1" : "0"}">${itemHtml}</li>`);
          items.push(
            `<li data-task="${checked ? "1" : "0"}" data-block-id="${itemId}">${itemHtml}</li>`,
          );
        } else {
          const itemHtml = inline(ln);
          legacyItems.push(`<li>${itemHtml}</li>`);
          items.push(`<li data-block-id="${itemId}">${itemHtml}</li>`);
        }
        i += 1;
      }
      const text = legacyItems.join("");
      const id = await computeBlockId("ul", text);
      blockIds.push(id);
      blockIds.push(...itemIds);
      out.push(`<ul data-block-id="${id}">${items.join("")}</ul>`);
      continue;
    }

    // Ordered list
    if (/^\d+\.\s+/.test(line)) {
      const items: string[] = [];
      const legacyItems: string[] = [];
      const itemIds: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i] ?? "")) {
        const ln = (lines[i] ?? "").replace(/^\d+\.\s+/, "");
        const itemId = await computeBlockId("li", ln);
        itemIds.push(itemId);
        const itemHtml = inline(ln);
        legacyItems.push(`<li>${itemHtml}</li>`);
        items.push(`<li data-block-id="${itemId}">${itemHtml}</li>`);
        i += 1;
      }
      const text = legacyItems.join("");
      const id = await computeBlockId("ol", text);
      blockIds.push(id);
      blockIds.push(...itemIds);
      out.push(`<ol data-block-id="${id}">${items.join("")}</ol>`);
      continue;
    }

    // Horizontal rule
    if (/^---\s*$/.test(line) || /^\*\*\*\s*$/.test(line)) {
      out.push("<hr />");
      i += 1;
      continue;
    }

    // GFM table (simple)
    if (line.includes("|") && lines[i + 1] && /^\s*\|?[\s:|-]+\|/.test(lines[i + 1] ?? "")) {
      const header = parseRow(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && (lines[i] ?? "").trim().startsWith("|")) {
        rows.push(parseRow(lines[i] ?? ""));
        i += 1;
      }
      const text = [header, ...rows].map((r) => r.join(" | ")).join("\n");
      const id = await computeBlockId("table", text);
      blockIds.push(id);
      const thead = `<thead><tr>${header.map((c) => `<th>${inline(c)}</th>`).join("")}</tr></thead>`;
      const tbody = `<tbody>${rows
        .map((r) => `<tr>${r.map((c) => `<td>${inline(c)}</td>`).join("")}</tr>`)
        .join("")}</tbody>`;
      out.push(`<table data-block-id="${id}">${thead}${tbody}</table>`);
      continue;
    }

    // Blank line
    if (line.trim() === "") {
      i += 1;
      continue;
    }

    // Paragraph (accumulate until blank line)
    const buf: string[] = [];
    while (i < lines.length && (lines[i] ?? "").trim() !== "") {
      const cur = lines[i] ?? "";
      // Stop at heading/list/code/quote start
      if (
        /^#{1,6}\s+/.test(cur) ||
        /^[-*+]\s+/.test(cur) ||
        /^\d+\.\s+/.test(cur) ||
        /^```/.test(cur) ||
        /^>\s?/.test(cur) ||
        /^---\s*$/.test(cur)
      ) {
        break;
      }
      buf.push(cur);
      i += 1;
    }
    if (buf.length > 0) {
      const text = buf.join(" ").trim();
      const id = await computeBlockId("p", text);
      blockIds.push(id);
      out.push(`<p data-block-id="${id}">${inline(text)}</p>`);
    }
  }

  return { html: out.join("\n"), frontmatter, blockIds };
}

function parseRow(line: string): string[] {
  const trimmed = line.trim().replace(/^\||\|$/g, "");
  return trimmed.split("|").map((c) => c.trim());
}

function inline(text: string): string {
  return text
    .replace(
      /!\[([^\]]*)\]\(([^)\s]+)(?:\s+"([^"]*)")?\)/g,
      (_, alt, src, title) =>
        `<img src="${escapeAttr(String(src))}" alt="${escapeAttr(String(alt))}" ${title ? `title="${escapeAttr(String(title))}"` : ""} />`,
    )
    .replace(
      /\[\[doc:([^\]]+)\]\]/g,
      (_, id) =>
        `<a data-docref="${escapeAttr(String(id))}" href="#doc:${escapeAttr(String(id))}">doc:${escapeAttr(String(id))}</a>`,
    )
    .replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      (_, label, href) => `<a href="${escapeAttr(String(href))}">${escapeHtml(String(label))}</a>`,
    )
    .replace(/`([^`]+)`/g, (_, code) => `<code>${escapeHtml(String(code))}</code>`)
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/(?<!\*)\*(?!\*)([^*]+)\*/g, "<em>$1</em>")
    .replace(/~~([^~]+)~~/g, "<del>$1</del>");
}

function escapeAttr(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
